import { gql, useMutation, useQuery } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconCode from '@apollo/icons/default/IconCode.svg';
import IconEdit from '@apollo/icons/default/IconEdit.svg';
import IconFloppyDisk from '@apollo/icons/default/IconFloppyDisk.svg';
import IconFloppyDiskWithPencil from '@apollo/icons/default/IconFloppyDiskWithPencil.svg';
import IconKeyboard from '@apollo/icons/default/IconKeyboard.svg';
import IconRun from '@apollo/icons/default/IconRun.svg';
import IconShare from '@apollo/icons/default/IconShare.svg';
import {
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
} from '@apollo/orbit';
import { Rocket2 } from '@apollo/space-kit/illustrations/Rocket2';
import classnames from 'classnames';
import { GraphQLSchema } from 'graphql';
import * as monacoEditor from 'monaco-editor';
import React from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';

import { isEmbeddableExplorerRoute } from 'src/app/embeddableExplorer/isEmbeddableExplorerRoute';
import { isEmbeddableSandboxRoute } from 'src/app/embeddableSandbox/isEmbeddableSandboxRoute';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { URLModal } from 'src/components/common/URLModal';
import { InformationalNudge } from 'src/components/informationalNudge/InformationalNudge';
import { useShowToast } from 'src/components/toast/Toast';
import { TooltipWithMutableContent } from 'src/components/TooltipWithMutableContent';
import { WrapMaybe } from 'src/components/wrapMaybe/WrapMaybe';
import { useIsCloudEndpointInitiatingContext } from 'src/hooks/useIsCloudEndpointInitiating';
import { toggleShortcutModal } from 'src/hooks/useKeypressListener/hotkeyDictionary';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import * as Types from 'src/lib/graphqlTypes/types';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import {
  hotkeys,
  useKeypressListener,
} from '../../../../hooks/useKeypressListener/useKeypressListener';
import { GraphRef } from '../../hooks/useGraphRef';
import { useGraphVisibility } from '../../hooks/useGraphVisibility';
import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';
import { useIsLoggedIn } from '../../hooks/useIsLoggedIn';
import { sandboxGraphRouteConfig } from '../../routes';
import {
  SandboxLogInButton,
  SandboxSignUpButtons,
} from '../../sandboxShared/SandboxSignUpButtons';
import { ExplorerBehavioralFlags } from '../explorerBehavioralFlags';
import {
  EditAction,
  executeEdits,
} from '../explorerDocs/getFieldToggle/executeEdits';
import { FieldTimingsPercentile } from '../explorerSettings/useExplorerSettings';
import { collectionEntryStateToInput } from '../helpers/collectionEntryStateToInput';
import { EditorBottomTab } from '../helpers/EditorBottomTab';
import { getUsedVariablesString } from '../helpers/jsonHelpers';
import { JSONObject } from '../helpers/types';
import { useActiveDefinitionDetails } from '../hooks/useActiveDefinitionDetails';
import { useAnalyticsContext } from '../hooks/useAnalyticsContext';
import {
  OperationState,
  useCopyLinkToOperation,
} from '../hooks/useCopyLinkToOperation';
import { useDerivedEditorValue } from '../hooks/useDerivedEditorValue';
import { CollectionEntryState } from '../hooks/useExplorerState/useEditorTabState/collectionEntryState';
import {
  EditorTabState,
  useTabbedSavedOperationLocalState,
} from '../hooks/useExplorerState/useEditorTabState/useEditorTabState';
import { ExplorerResult } from '../hooks/useExplorerState/useExplorerResponses';
import { useHeadersManagerContext } from '../hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import { printHeadersState } from '../hooks/useLoadExplorerStateFromURL';

import { useEditorRefactorsRef } from './editorRefactors';
import { getLintWarnings } from './getLintWarnings';
import { GraphqlEditor } from './graphqlEditor/GraphqlEditor';

export const OperationsPane = ({
  graphRef,
  graphURL,
  submitOperation,
  editor,
  variablesEditor,
  preflightOperationScriptEditor,
  postflightOperationScriptEditor,
  editorTabState,
  includeCookies,
  schema,
  hasSupergraphSchema,
  isCloudSupergraph,
  setActiveEditorBottomTab,
  submitForResponseHints,
  responseHints,
  fieldTimingsPercentile,
  setKeyboardModal,
  shouldManageVariables,
  operationResult,
  explorerBehavioralFlags,
  canBeEmbedded,
}: {
  graphRef: GraphRef | null;
  setKeyboardModal: (isOpen: boolean) => void;
  graphURL?: null | string;
  submitOperation: (args: {
    operationName: string | undefined;
    body: string;
    isSubscription: boolean;
  }) => void;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  variablesEditor: monacoEditor.editor.IStandaloneCodeEditor;
  preflightOperationScriptEditor: monacoEditor.editor.IStandaloneCodeEditor;
  postflightOperationScriptEditor: monacoEditor.editor.IStandaloneCodeEditor;
  editorTabState: EditorTabState;
  includeCookies: boolean;
  schema: GraphQLSchema;
  hasSupergraphSchema: boolean;
  isCloudSupergraph: boolean;
  setActiveEditorBottomTab: (tab: EditorBottomTab) => void;
  submitForResponseHints: (
    operation: string,
  ) => Promise<JSONObject | undefined>;
  responseHints: Types.ResponseHints | null;
  fieldTimingsPercentile: FieldTimingsPercentile;
  shouldManageVariables: boolean;
  operationResult: ExplorerResult;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  canBeEmbedded: boolean;
}) => {
  const trackCustomEvent = useTrackCustomEvent();
  const { track } = useAnalyticsContext();

  const location = useLocation();
  const history = useHistory();
  const { isLoggedIn } = useIsLoggedIn();

  const { selectedTab, dispatchSavedOperationLocalStateAction } =
    editorTabState;
  const { savedOperationLocalState } = useTabbedSavedOperationLocalState();
  const savedOperation =
    selectedTab === undefined
      ? undefined
      : savedOperationLocalState[selectedTab];
  const operationIsModified =
    savedOperation &&
    (savedOperation.operationModified ||
      savedOperation.variablesModified ||
      savedOperation.headersModified ||
      savedOperation.preflightOperationScriptModified ||
      savedOperation.postflightOperationScriptModified);

  const activeDefinition = useActiveDefinitionDetails(editor, schema);

  const { tabbedHeaderDefinitions, checkedHeaders } =
    useHeadersManagerContext();
  const [explorerLinkSettings] = useLocalStorage('explorerLinkSettings');

  const operationState = React.useCallback(
    (): OperationState =>
      savedOperation
        ? {
            type: 'saved',
            savedCollectionId: savedOperation.collectionId,
            savedCollectionEntryId: savedOperation.collectionEntryId,
            sharedCollectionOriginVariantRef:
              savedOperation.sharedCollectionOriginVariantRef,
            isShared: savedOperation.isShared,
          }
        : {
            type: 'local',
            document: editor.getValue(),
            variables: variablesEditor.getValue(),
            headers: printHeadersState(tabbedHeaderDefinitions),
            preflightOperationScript: preflightOperationScriptEditor.getValue(),
            postflightOperationScript:
              postflightOperationScriptEditor.getValue(),
          },
    [
      savedOperation,
      editor,
      variablesEditor,
      tabbedHeaderDefinitions,
      preflightOperationScriptEditor,
      postflightOperationScriptEditor,
    ],
  );

  const operationStateInput = React.useCallback(
    (): CollectionEntryState => ({
      operation: editor.getValue(),
      variables: variablesEditor.getValue(),
      headers: tabbedHeaderDefinitions.filter(
        (header) => !!header.headerName.trim(),
      ),
      preflightOperationScript: preflightOperationScriptEditor.getValue(),
      postflightOperationScript: postflightOperationScriptEditor.getValue(),
    }),
    [
      editor,
      variablesEditor,
      tabbedHeaderDefinitions,
      preflightOperationScriptEditor,
      postflightOperationScriptEditor,
    ],
  );

  const baseCopyLinkToOperation = useCopyLinkToOperation();
  const copyLinkToOperation = React.useCallback(() => {
    baseCopyLinkToOperation(operationState());
  }, [baseCopyLinkToOperation, operationState]);

  const wrappedExecuteEdits = React.useMemo(() => {
    return (action: EditAction) => {
      executeEdits(
        action,
        editor,
        variablesEditor,
        setActiveEditorBottomTab,
        true,
      );
      editor.getAction('editor.action.formatDocument')?.run();
      variablesEditor.getAction('editor.action.formatDocument')?.run();
    };
  }, [editor, variablesEditor, setActiveEditorBottomTab]);

  const editorRefactorsRef = useEditorRefactorsRef({
    operationEditor: editor,
    variablesEditor,
    executeEdits: wrappedExecuteEdits,
    schema,
  });

  const stableGetLintWarnings = React.useCallback(
    (editorValue: string) =>
      schema
        ? getLintWarnings({
            src:
              activeDefinition?.activePartsOfDocumentWithUnusedVariables ??
              editorValue,
            schema,
          })
        : null,
    [schema, activeDefinition],
  );
  const lintWarnings = useDerivedEditorValue({
    editor,
    getValue: stableGetLintWarnings,
    withPosition: false,
  });

  const submitOperationWithAssumedName = () => {
    if (!activeDefinition?.isValid) {
      // TODO (jason) we should do something here, maybe focus the invalid
      // portion in the editor
      return;
    }

    // TODO: if there is more than one operation, ask users to choose one
    return submitOperation({
      operationName: activeDefinition?.name ?? undefined,
      body: activeDefinition.activePartsOfDocument ?? '',
      isSubscription: activeDefinition.isSubscription,
    });
  };

  const [updateOperationMutation] = useMutation<
    GraphQLTypes.UpdateOperation,
    GraphQLTypes.UpdateOperationVariables
  >(gql`
    mutation UpdateOperation(
      $collectionId: ID!
      $collectionEntryId: ID!
      $operationInput: OperationCollectionEntryStateInput!
    ) {
      operationCollection(id: $collectionId) {
        operation(id: $collectionEntryId) {
          ... on OperationCollectionEntryMutation {
            updateValues(operationInput: $operationInput) {
              ... on OperationCollectionEntry {
                id
                lastUpdatedAt
                lastUpdatedBy {
                  id
                  name
                }
                currentOperationRevision {
                  body
                  variables
                  headers {
                    name
                    value
                  }
                  script
                  postflightOperationScript
                }
                collection {
                  id
                  isShared
                  isSandbox
                }
              }
              ... on Error {
                message
              }
            }
          }
        }
      }
    }
  `);

  const { data } = useQuery<
    GraphQLTypes.OperationsPaneCollectionDetailsQuery,
    GraphQLTypes.OperationsPaneCollectionDetailsQueryVariables
  >(
    gql`
      query OperationsPaneCollectionDetailsQuery($operationCollectionId: ID!) {
        operationCollection(id: $operationCollectionId) {
          ... on OperationCollection {
            id
            permissions {
              canEditOperations
            }
          }
        }
      }
    `,
    {
      skip: !savedOperation,
      variables: {
        operationCollectionId: savedOperation?.collectionId ?? '',
      },
    },
  );

  const canEditSavedEntryCollection =
    data?.operationCollection.__typename === 'OperationCollection' &&
    data.operationCollection.permissions.canEditOperations;

  const { showToasts } = useShowToast();

  const isSandboxRoute =
    useRouteMatch(sandboxGraphRouteConfig.definition) !== null;
  const { currentExplorerRouteConfig } = useInternalGraphLinking();
  const { showSupergraphEndpointSuccessNudge } = useRouteParams(
    currentExplorerRouteConfig,
  );

  const shouldShowShareAsEmbedOption =
    !explorerBehavioralFlags.cannotBeEmbedded;

  const openSaveOperationModal = React.useCallback(async () => {
    if (!isLoggedIn) {
      history.push(
        setOverlayVisibleLocation({
          location,
          overlayName: Config.modals.saveCollectionLoginPrompt,
        }),
      );
      return;
    }
    if (!selectedTab) {
      return;
    }
    const collectionEntryState = operationStateInput();
    history.replace({
      ...setOverlayVisibleLocation({
        location,
        overlayName: Config.modals.saveOperationCollectionEntry,
        additionalState: {
          operationTabId: selectedTab,
          collectionEntryState,
          graphRef,
        },
      }),
    });
  }, [
    history,
    location,
    operationStateInput,
    selectedTab,
    graphRef,
    isLoggedIn,
  ]);

  const [savingOperation, setSavingOperation] = React.useState(false);
  const saveOrUpdateOperation = React.useCallback(async () => {
    if (!isLoggedIn) {
      history.push(
        setOverlayVisibleLocation({
          location,
          overlayName: Config.modals.saveCollectionLoginPrompt,
        }),
      );
      return;
    }
    if (!selectedTab) {
      return;
    }
    if (savedOperation) {
      setSavingOperation(true);
      const collectionEntryState = operationStateInput();

      // filter out headers that were injected from useHandleOpenSavedOperation
      // from shared headers from the origin variant. We don't want to save
      // these as new headers on this operation
      const { headers: headersPreFilter, ...operationInputRest } =
        collectionEntryStateToInput(collectionEntryState);
      const sharedCollectionOriginVariantHeaders =
        savedOperation.sharedCollectionOriginVariantHeaders;
      const headersPostFilter = sharedCollectionOriginVariantHeaders
        ? headersPreFilter?.filter(
            (header) =>
              !sharedCollectionOriginVariantHeaders.some(
                (sharedHeader) =>
                  sharedHeader.headerName === header.name &&
                  sharedHeader.value === header.value,
              ),
          )
        : headersPreFilter;

      const result = await updateOperationMutation({
        variables: {
          collectionId: savedOperation.collectionId,
          collectionEntryId: savedOperation.collectionEntryId,
          operationInput: { ...operationInputRest, headers: headersPostFilter },
        },
      });
      const updateOperation = result.data?.operationCollection?.operation;
      const updateValues =
        updateOperation?.__typename === 'OperationCollectionEntryMutation'
          ? updateOperation.updateValues
          : undefined;
      if (updateValues?.__typename !== 'OperationCollectionEntry') {
        showToasts({
          heading: updateValues
            ? `Error saving operation: ${updateValues.message}`
            : 'Error saving operation',
          level: 'warn',
          message: false,
        });
        return;
      }
      dispatchSavedOperationLocalStateAction({
        type: 'SaveOperation',
        collectionId: savedOperation.collectionId,
        collectionEntryId: savedOperation.collectionEntryId,
        operationTabId: selectedTab,
        collectionEntryState,
        isShared: savedOperation.isShared,
        sharedCollectionOriginVariantHeaders:
          savedOperation.sharedCollectionOriginVariantHeaders,
        sharedCollectionOriginVariantRef:
          savedOperation.sharedCollectionOriginVariantRef,
      });
      const collection = updateValues.collection;
      trackCustomEvent({
        action: 'update_collection_entry',
        category: 'Operation Collections',
        label: collection.isSandbox
          ? 'sandbox'
          : collection.isShared
            ? 'shared'
            : 'personal',
      });
      setSavingOperation(false);
    } else {
      trackCustomEvent({
        action: 'open_save_collection_entry_modal',
        category: 'Operation Collections',
      });
      openSaveOperationModal();
    }
  }, [
    isLoggedIn,
    selectedTab,
    savedOperation,
    history,
    location,
    operationStateInput,
    updateOperationMutation,
    dispatchSavedOperationLocalStateAction,
    trackCustomEvent,
    showToasts,
    openSaveOperationModal,
  ]);

  const graphVisibilityType = useGraphVisibility();
  const isPublicRoute = graphVisibilityType === 'public';

  const saveDisabled =
    savedOperation && (!operationIsModified || !canEditSavedEntryCollection);

  useKeypressListener(hotkeys.submitOperation, () => {
    submitOperationWithAssumedName();
  });

  useKeypressListener(hotkeys.saveOperation, () => {
    if (explorerBehavioralFlags.showOperationCollections) {
      if (!saveDisabled) {
        saveOrUpdateOperation();
      } else if (!canEditSavedEntryCollection) {
        showToasts({
          heading: 'You do not have permission to edit this operation',
          level: 'warn',
          stableId: 'cannot-save-operation',
          message: false,
        });
      }
    }
  });

  useKeypressListener(hotkeys.saveOperationAs, () => {
    if (explorerBehavioralFlags.showOperationCollections) {
      openSaveOperationModal();
    }
  });

  useKeypressListener(hotkeys.openCommandPalette, () => {
    track('Hotkey Open Command Palette');
  });

  const {
    loading: routerIsInitiatingLoading,
    routerIsInitiating: routerIsInitiatingBase,
  } = useIsCloudEndpointInitiatingContext();
  const routerIsInitiating =
    isCloudSupergraph && !routerIsInitiatingLoading && routerIsInitiatingBase;

  return (
    <div className="flex h-full grow flex-col rounded-t-lg bg-primary-inverted">
      <div className="mx-4 mb-2 mt-4 flex flex-none items-center gap-2">
        <span
          className="ml-2 flex-1 text-base font-semibold"
          role="heading"
          aria-level={2}
        >
          Operation
        </span>
        {explorerBehavioralFlags.shouldShowCopyLink && (
          <ButtonGroup isAttached>
            <TooltipWithMutableContent
              initialContent={
                explorerLinkSettings.preferencesSaved
                  ? 'Copy shareable operation link'
                  : 'Edit shareable operation link settings'
              }
            >
              {({ setContent, resetContent }) => (
                <IconButton
                  aria-label="Copy shareable operation link"
                  type="button"
                  size="sm"
                  variant="hidden"
                  icon={<IconShare />}
                  onClick={() => {
                    if (explorerLinkSettings.preferencesSaved) {
                      copyLinkToOperation();
                      setContent('Link copied to clipboard');
                    } else {
                      history.push({
                        ...setOverlayVisibleLocation({
                          location,
                          overlayName:
                            Config.modals.explorerShareableLinkSettings,
                        }),
                        state: { operationState: operationState() },
                      });
                    }
                  }}
                  onMouseEnter={() => resetContent()}
                />
              )}
            </TooltipWithMutableContent>
            {shouldShowShareAsEmbedOption && (
              <Menu placement="bottom-end" autoSelect={false}>
                <Tooltip label="More share options">
                  <MenuButton
                    as={IconButton}
                    aria-label="Share options dropdown"
                    data-testid="share_operation_dropdown"
                    className="rounded-l-none"
                    type="button"
                    variant="hidden"
                    size="sm"
                  >
                    <IconChevronDown />
                  </MenuButton>
                </Tooltip>
                <MenuContent>
                  <MenuList>
                    <MenuItem
                      as={Link}
                      to={{
                        ...setOverlayVisibleLocation({
                          location,
                          overlayName: isSandboxRoute
                            ? Config.modals.embedSandbox
                            : Config.modals.embedExplorer,
                        }),
                        state: {
                          embedModalTabState: activeDefinition && {
                            document:
                              activeDefinition.activePartsOfDocument?.trim(),
                            variables: getUsedVariablesString(
                              schema,
                              editor,
                              variablesEditor,
                              editor.getPosition() ??
                                new monacoEditor.Position(1, 1),
                            ),
                            headers: JSON.stringify(checkedHeaders),
                            // Embedded explorer doesn't consume consume any includeCookies config options
                            includeCookies: isSandboxRoute
                              ? includeCookies.toString()
                              : undefined,
                          },
                        },
                      }}
                      icon={<IconCode />}
                    >
                      Share as embed
                    </MenuItem>
                    <MenuItem
                      as={Link}
                      to={setOverlayVisibleLocation({
                        location,
                        overlayName:
                          Config.modals.explorerShareableLinkSettings,
                      })}
                      icon={<IconEdit />}
                    >
                      Edit shareable link settings
                    </MenuItem>
                  </MenuList>
                </MenuContent>
              </Menu>
            )}
          </ButtonGroup>
        )}
        {!isPublicRoute && explorerBehavioralFlags.showOperationCollections && (
          <ButtonGroup isAttached>
            <Tooltip
              label={
                savingOperation
                  ? 'Saving...'
                  : !savedOperation
                    ? 'Save operation'
                    : !operationIsModified
                      ? 'No new changes to save.'
                      : !canEditSavedEntryCollection
                        ? "You cannot directly make changes to this operation. Select 'Save As' to make a copy."
                        : 'Update saved operation'
              }
            >
              <IconButton
                aria-label="Save"
                variant="hidden"
                isDisabled={saveDisabled}
                type="button"
                size="sm"
                data-testid="save_operation_button"
                isLoading={savingOperation}
                icon={<IconFloppyDisk />}
                onClick={() => saveOrUpdateOperation()}
              />
            </Tooltip>
            <Menu placement="bottom-end" autoSelect={false}>
              <Tooltip label="More save options">
                <MenuButton
                  as={IconButton}
                  aria-label="Save options dropdown"
                  data-testid="save_operation_dropdown"
                  variant="hidden"
                  size="sm"
                  type="button"
                >
                  <IconChevronDown />
                </MenuButton>
              </Tooltip>
              <MenuContent>
                <MenuList>
                  <MenuItem
                    onClick={() => openSaveOperationModal()}
                    icon={<IconFloppyDiskWithPencil />}
                  >
                    Save As
                  </MenuItem>
                </MenuList>
              </MenuContent>
            </Menu>
          </ButtonGroup>
        )}
        <Tooltip
          label={
            // TODO(Maya): remove this when router can return non-null for
            // null apiIdentities. Public routes will always show router
            // as initiating b/c GraphVariant.router always comes back null right now
            routerIsInitiating && !isPublicRoute ? (
              `Your new supergraph endpoint is being set up, which can take a few
            seconds. You’ll be notified as soon as it’s ready!`
            ) : activeDefinition?.isValid ? (
              activeDefinition.isSubscription &&
              !explorerBehavioralFlags.subscriptionsSupported ? (
                isEmbeddableExplorerRoute() && !graphRef ? (
                  `UNABLE TO RUN - Subscriptions are not supported for this embed, since you are not embedding with a registered Studio graph.`
                ) : (
                  `UNABLE TO RUN - Subscriptions are not supported in this version of the embed. Try updating to the latest version.`
                )
              ) : (
                `Submit Operation ( ${hotkeys.submitOperation.label} )`
              )
            ) : lintWarnings?.parseErrors?.length ? (
              <>
                <div key="header">UNABLE TO RUN:</div>
                {lintWarnings.parseErrors.map((error) => (
                  <div
                    key={`${error.location.line}:${error.location.column}:${error.message}`}
                  >
                    ln:{error.location.line} - {error.message}
                  </div>
                ))}
              </>
            ) : (
              `UNABLE TO RUN${
                activeDefinition ? '' : ' - please select an operation'
              }`
            )
          }
        >
          <WrapMaybe
            on={showSupergraphEndpointSuccessNudge === 'true'}
            element={
              <InformationalNudge
                isOpen={showSupergraphEndpointSuccessNudge === 'true'}
                title="Your supergraph endpoint is ready!"
                placement="bottom"
                description="Press “Run” to start querying your graph and see it in action! 🎉"
                onClose={() =>
                  history.push({
                    ...history.location,
                    state: {
                      ...(typeof history.location.state === 'object'
                        ? history.location.state
                        : {}),
                      showSupergraphEndpointSuccessNudge: 'false',
                    },
                  })
                }
              />
            }
          >
            <Button
              data-testid="run_operation_button"
              variant="primary"
              size="sm"
              type="button"
              isDisabled={
                (routerIsInitiating && !isPublicRoute) ||
                !activeDefinition?.isValid ||
                (activeDefinition.isSubscription &&
                  !explorerBehavioralFlags.subscriptionsSupported)
              }
              onClick={submitOperationWithAssumedName}
              leftIcon={
                operationResult?.type === 'loading' ||
                (operationResult?.type === 'response' &&
                  operationResult.data.hasNext) ? (
                  <Spinner size="tiny" />
                ) : (
                  <IconRun />
                )
              }
            >
              <span className="truncate">
                {activeDefinition?.name || 'Run'}
              </span>
            </Button>
          </WrapMaybe>
        </Tooltip>
      </div>
      <div className="relative size-full" data-testid="operation_editor">
        <GraphqlEditor
          className="xxx-monaco-editor-pane absolute size-full flex-1 overflow-hidden"
          graphRef={graphRef}
          graphURL={graphURL}
          editor={editor}
          variablesEditor={variablesEditor}
          schema={schema}
          hasSupergraphSchema={hasSupergraphSchema}
          lintWarnings={lintWarnings}
          copyLinkToOperation={copyLinkToOperation}
          editorRefactorsRef={editorRefactorsRef}
          setActiveEditorBottomTab={setActiveEditorBottomTab}
          submitOperation={submitOperation}
          submitForResponseHints={submitForResponseHints}
          responseHints={responseHints}
          fieldTimingsPercentile={fieldTimingsPercentile}
          shouldManageVariables={shouldManageVariables}
          operationResult={operationResult}
          explorerBehavioralFlags={explorerBehavioralFlags}
          canBeEmbedded={canBeEmbedded}
          editorTabState={editorTabState}
          includeCookies={includeCookies}
        />
        {explorerBehavioralFlags.shouldShowKeyboardShortcutIcon && (
          <div className="absolute bottom-4 right-4 z-20">
            <Tooltip
              label={`Show keyboard shortcuts (${toggleShortcutModal.label})`}
            >
              <IconButton
                aria-label={`Show keyboard shortcuts (${toggleShortcutModal.label})`}
                type="button"
                size="sm"
                variant="hidden"
                icon={<IconKeyboard />}
                onClick={() => setKeyboardModal(true)}
              />
            </Tooltip>
          </div>
        )}
      </div>
      <URLModal name={Config.modals.saveCollectionLoginPrompt}>
        {({ isOpen, closeModal }) => {
          const isEmbedded =
            isEmbeddableExplorerRoute() || isEmbeddableSandboxRoute();

          return (
            <Modal isOpen={isOpen} onClose={closeModal} size="lg">
              <ModalOverlay />
              <ModalContent>
                <ModalBody>
                  <div className="flex flex-col items-center">
                    <Rocket2
                      className={classnames(
                        '-mt-4 w-40 text-inverted',
                        '[&_[fill="#74B0F4"]]:fill-transparent [&_[fill="#F18686"]]:fill-transparent [&_[stroke="#464646"]]:stroke-blilet-light',
                        '[&_g.stars_circle[fill="#CAD0D8"]]:fill-blilet [&_g.stars_path[fill="#CAD0D8"]]:fill-transparent [&_g.stars_path[fill="#CAD0D8"]]:stroke-blilet',
                        '[&_g[fill-rule="evenodd"]_>_g_circle[fill="#CAD0D8"]]:fill-blilet [&_g[fill-rule="evenodd"]_>_g_path[fill="#CAD0D8"]]:fill-transparent [&_g[fill-rule="evenodd"]_>_g_path[fill="#CAD0D8"]]:stroke-blilet',
                      )}
                    />
                    <div className="mt-2 text-xl font-semibold ">
                      Log in to your Studio account
                    </div>
                    <div className="mb-5 text-base text-secondary">
                      to save your operation
                    </div>
                    {isEmbedded ? (
                      <SandboxLogInButton
                        userSegmentType="sandbox_operation_collections"
                        className="mt-8"
                      />
                    ) : (
                      <SandboxSignUpButtons userSegmentType="sandbox_operation_collections" />
                    )}
                  </div>
                </ModalBody>
              </ModalContent>
            </Modal>
          );
        }}
      </URLModal>
    </div>
  );
};
